import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SearchIcon from '@mui/icons-material/Search';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StorageIcon from '@mui/icons-material/Storage';
import AppsIcon from '@mui/icons-material/Apps';
import { useConfirm } from 'material-ui-confirm';
import { useApiContext } from '@/contexts/apiContext';
import ErrorMessage from '@/components/errorMessage';
import { EntityType } from '@/components/entities/entityType';
import { EventPayloadViewer } from '@/components/viewers';
import {
  ExecutionStep,
  ExecutionStepResult,
  ItemProcessingStatusDto,
  PipelineExecutionState,
} from '@/interfaces/pipelineExecution.interface';
import { formatRunDuration } from '@/utils';

interface UserAppStepItemsViewProps {
  step: ExecutionStep;
  pipelineExecutionState?: PipelineExecutionState;
  onRestartFromPoint?: (step: ExecutionStep, eventDataId?: string | number) => void;
  onExecuteStep?: (
    step: any,
    options?: { inputOverride?: any; reingest?: boolean; suppressEvents?: boolean },
  ) => Promise<void> | void;
  suppressEvents?: boolean;
  onToggleSuppressEvents?: (suppress: boolean) => void;
  stepExecutionResponse?: ExecutionStepResult | null;
  onClearStepExecutionResponse?: () => void;
  onChange?: () => void;
  isExecuting?: boolean;
}

const formatBytes = (bytes?: number): string => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

/**
 * Component for an Individual Event Data Record
 */
const UserAppEventCard: React.FC<{
  ev: any;
  onDelete: (ev: any) => void;
}> = ({ ev, onDelete }) => {
  const [showInputContext, setShowInputContext] = useState(false);
  const [copiedId, setCopiedId] = useState<string | number | null>(null);

  const payload = ev.eventPayload ?? ev.result ?? ev;
  const isEvFailed =
    ev.eventName?.toLowerCase().includes('fail') || ev.eventName?.toLowerCase().includes('error') || Boolean(ev.error);

  const handleCopy = (text: string, id: string | number) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        bgcolor: '#ffffff',
        borderRadius: 1,
        border: isEvFailed ? '1px solid #fecaca' : '1px solid #e2e8f0',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <Stack spacing={1} sx={{ width: '100%', maxWidth: '100%' }}>
        {/* Event Header Bar */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="nowrap"
          gap={1}
          sx={{ width: '100%', minWidth: 0 }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}
          >
            <Chip
              label={ev.eventName || 'Output Event'}
              size="small"
              color={isEvFailed ? 'error' : 'primary'}
              variant="filled"
              sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }}
            />
            {ev.id && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                Event ID: <code>{ev.id}</code>
              </Typography>
            )}
            {ev.dateTime && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                • {new Date(ev.dateTime).toLocaleString()}
              </Typography>
            )}
          </Stack>

          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0, ml: 'auto' }}>
            <Tooltip title={copiedId === ev.id ? 'Copied!' : 'Copy Payload JSON'}>
              <IconButton
                size="small"
                onClick={() => handleCopy(JSON.stringify(payload, null, 2), ev.id)}
                sx={{ p: 0.25 }}
              >
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete this event record">
              <IconButton size="small" color="error" onClick={() => onDelete(ev)} sx={{ p: 0.25 }}>
                <DeleteOutlineIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* PRIMARY: Event Output Payload */}
        <EventPayloadViewer payload={ev.eventPayload ?? ev.result ?? ev} label="Output Payload" showMimeType={true} />

        {/* SECONDARY: Inbound Input Context (Collapsible) */}
        {ev.inputEvent && (
          <Box
            sx={{ width: '100%', maxWidth: '100%', border: '1px solid #f1f5f9', borderRadius: 1, bgcolor: '#fafafa' }}
          >
            <Box
              onClick={() => setShowInputContext(!showInputContext)}
              sx={{
                px: 1,
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': { bgcolor: '#f1f5f9' },
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', fontSize: '0.7rem' }}>
                Inbound Input Context (Trigger / Prompt)
              </Typography>
              <IconButton size="small" sx={{ p: 0.25 }}>
                {showInputContext ? (
                  <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
                ) : (
                  <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Box>
            <Collapse in={showInputContext} timeout="auto" unmountOnExit>
              <Box sx={{ p: 1, pt: 0.5, bgcolor: '#f8fafc', borderTop: '1px solid #f1f5f9', overflow: 'auto' }}>
                <EventPayloadViewer payload={ev.inputEvent} label="Inbound Input Context" showMimeType={true} />
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Error Banner if event failed */}
        {ev.error && <ErrorMessage error={ev.error} />}
      </Stack>
    </Paper>
  );
};

/**
 * Expandable Row for a Single User App Source Item
 */
const UserAppItemRow: React.FC<{
  item: any;
  itemIndex: number;
  pipelineInstanceId?: string;
  onRestartFromPoint?: (step: ExecutionStep, eventDataId?: string | number) => void;
  onItemDeleted?: () => void;
  onChange?: () => void;
  step: ExecutionStep;
}> = ({ item, itemIndex, pipelineInstanceId, onRestartFromPoint, onItemDeleted, onChange, step }) => {
  const apiContext = useApiContext();
  const confirm = useConfirm();
  const [expanded, setExpanded] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [eventsData, setEventsData] = useState<any[]>([]);

  // Item deletion error (Pipeline scope)
  const [deleteItemError, setDeleteItemError] = useState<string | null>(null);

  const handleDeleteItem = async () => {
    if (!pipelineInstanceId) return;
    try {
      await confirm({
        title: 'Delete Item Output from Pipeline',
        description: `Are you sure you want to delete the output for "${item.itemName || `Item #${item.dataSourceItemId || item.id}`}"? This will remove the processing status and generated event data from its source agent and all subsequent downstream agents in the pipeline. The source data item will remain in the data source.`,
        confirmationText: 'Delete Output',
        cancellationText: 'Cancel',
        confirmationButtonProps: { color: 'error', variant: 'contained' },
      });
      setDeleteItemError(null);
      await apiContext.deleteUserAppInstanceItemProcessingStatus(
        step.entityDefId,
        step.entityId,
        item.id,
        pipelineInstanceId,
      );
      if (onItemDeleted) onItemDeleted();
      if (onChange) onChange();
    } catch (err: any) {
      if (err) {
        console.error('Error deleting item from pipeline:', err);
        setDeleteItemError(err?.response?.data?.message || err?.message || 'Failed to delete item from pipeline.');
      }
    }
  };

  const fetchEvents = async () => {
    setLoadingEvents(true);
    try {
      let events: any[] = [];
      const agentDefId = item.sourceEntityDefId || item.entityDefId;
      const agentId = item.sourceEntityId || item.entityId;

      if (agentDefId && agentId) {
        events = await apiContext.getAgentInstanceEventsData(
          agentDefId,
          agentId,
          item.dataSourceDefId,
          item.dataSourceInstanceId,
          String(item.dataSourceItemId || item.dataSourceItemGroup || item.id),
          pipelineInstanceId,
        );
      } else {
        events = await apiContext.getDataSourceItemEventsData(
          item.dataSourceDefId,
          item.dataSourceInstanceId,
          String(item.dataSourceItemId || item.dataSourceItemGroup || item.id),
          false,
          pipelineInstanceId,
        );
      }
      setEventsData(events || []);
    } catch (err) {
      console.error('Error fetching event data for item:', err);
    } finally {
      setLoadingEvents(false);
    }
  };

  const handleToggle = () => {
    if (!expanded && eventsData.length === 0) {
      fetchEvents();
    }
    setExpanded(!expanded);
  };

  const handleDeleteEvent = async (ev: any) => {
    if (!ev?.id) return;
    try {
      await confirm({
        title: 'Delete Event Data',
        description: `Are you sure you want to permanently delete this event record (ID: ${ev.id})? This action cannot be undone.`,
        confirmationText: 'Delete',
        cancellationText: 'Cancel',
        confirmationButtonProps: { color: 'error', variant: 'contained' },
      });
      await apiContext.deleteUserAppInstanceEventData(
        item.sourceEntityDefId || item.entityDefId,
        item.sourceEntityId || item.entityId,
        ev.id,
      );
      const remaining = (eventsData || []).filter((e) => e.id !== ev.id);
      setEventsData(remaining);
      if (remaining.length === 0 && onItemDeleted) {
        onItemDeleted();
      } else {
        await fetchEvents();
      }
      if (onChange) onChange();
    } catch (err) {
      if (err) {
        console.error('Failed to delete event data:', err);
      }
    }
  };

  const isFailed =
    item.status?.toLowerCase() === 'failed' ||
    item.status?.toLowerCase() === 'error' ||
    Boolean(item.error) ||
    Boolean(item.dsError);

  const durationStr = formatRunDuration(item.lastProcessedStartDate, item.lastProcessedEndDate);

  return (
    <>
      <TableRow
        hover
        sx={{
          '& > *': { borderBottom: 'unset' },
          bgcolor: expanded ? '#f8fafc' : 'inherit',
          cursor: 'pointer',
        }}
        onClick={handleToggle}
      >
        {/* Expand / Collapse Icon */}
        <TableCell sx={{ py: 0.75, px: 1, width: 36 }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleToggle();
            }}
          >
            {expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>

        {/* Item Name */}
        <TableCell sx={{ py: 0.75, px: 1, maxWidth: 220 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography
              variant="body2"
              noWrap
              sx={{
                fontWeight: 600,
                fontSize: '0.8rem',
                color: isFailed ? 'error.main' : 'text.primary',
                maxWidth: 190,
              }}
              title={item.itemName || `Item #${item.dataSourceItemId || item.id}`}
            >
              {item.itemName || `Item #${item.dataSourceItemId || item.id}`}
            </Typography>
            {item.itemSize && item.itemSize > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                ({formatBytes(item.itemSize)})
              </Typography>
            )}
          </Stack>
        </TableCell>

        {/* Source Agent */}
        <TableCell sx={{ py: 0.75, px: 1 }}>
          <Chip
            icon={<SmartToyIcon sx={{ fontSize: '12px !important' }} />}
            label={item.sourceAgentName || item.sourceEntityName || 'Agent'}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, borderColor: '#cbd5e1', bgcolor: '#f8fafc' }}
          />
        </TableCell>

        {/* Data Source Name */}
        <TableCell sx={{ py: 0.75, px: 1 }}>
          <Chip
            icon={<StorageIcon sx={{ fontSize: '12px !important' }} />}
            label={item.dataSourceName || item.dataSourceInstanceId || 'Data Source'}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 500, borderColor: '#e2e8f0' }}
          />
        </TableCell>

        {/* Status */}
        <TableCell sx={{ py: 0.75, px: 1 }}>
          {isFailed ? (
            <Chip
              icon={<ErrorIcon sx={{ fontSize: '13px !important' }} />}
              label="Failed"
              size="small"
              color="error"
              sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }}
            />
          ) : (
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: '13px !important' }} />}
              label="Success"
              size="small"
              color="success"
              sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }}
            />
          )}
        </TableCell>

        {/* Duration / Processed Time */}
        <TableCell sx={{ py: 0.75, px: 1 }}>
          <Stack spacing={0.25}>
            {durationStr !== '-' && (
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.72rem' }}>
                {durationStr}
              </Typography>
            )}
            {item.lastProcessedStartDate && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                {new Date(item.lastProcessedStartDate).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </Typography>
            )}
          </Stack>
        </TableCell>

        {/* Actions */}
        <TableCell sx={{ py: 0.75, px: 1, width: 80, textAlign: 'right' }}>
          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
            {onRestartFromPoint && (
              <Tooltip title="Re-run pipeline from this item">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRestartFromPoint(step, item.id);
                  }}
                  sx={{ p: 0.5 }}
                >
                  <PlayArrowIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {pipelineInstanceId && (
              <Tooltip title="Delete Item from Pipeline">
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteItem();
                  }}
                  sx={{ p: 0.5 }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </TableCell>
      </TableRow>

      {/* Expanded Row: Event Data Records */}
      <TableRow sx={{ bgcolor: '#f8fafc' }}>
        <TableCell
          colSpan={7}
          sx={{ p: 0, borderBottom: expanded ? '1px solid #e2e8f0' : 'none', maxWidth: 0, width: '100%' }}
        >
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box
              sx={{
                p: 2,
                bgcolor: '#f1f5f9',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                overflow: 'hidden',
              }}
            >
              <Stack spacing={1.5} sx={{ width: '100%', maxWidth: '100%' }}>
                {deleteItemError && (
                  <Box sx={{ mb: 1 }}>
                    <ErrorMessage error={{ message: deleteItemError }} />
                  </Box>
                )}
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}
                  >
                    Source Processing Events ({eventsData.length})
                  </Typography>
                  {item.error && (
                    <Chip
                      label={item.error}
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.68rem', maxWidth: 300 }}
                    />
                  )}
                </Stack>

                {loadingEvents ? (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1.5, justifyContent: 'center' }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">
                      Loading event data...
                    </Typography>
                  </Stack>
                ) : eventsData.length === 0 ? (
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#fff', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      No event records generated yet for this item.
                    </Typography>
                  </Paper>
                ) : (
                  eventsData.map((ev, evIdx) => (
                    <UserAppEventCard key={ev.id ?? evIdx} ev={ev} onDelete={handleDeleteEvent} />
                  ))
                )}
              </Stack>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

/**
 * Main User App Step Items View Component
 */
export const UserAppStepItemsView: React.FC<UserAppStepItemsViewProps> = ({
  step,
  pipelineExecutionState,
  onRestartFromPoint,
  onExecuteStep,
  suppressEvents,
  onToggleSuppressEvents,
  stepExecutionResponse,
  onClearStepExecutionResponse,
  onChange,
  isExecuting,
}) => {
  const apiContext = useApiContext();

  const [rawItems, setRawItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [selectedSourceAgentId, setSelectedSourceAgentId] = useState<string>('all');
  const [selectedDataSourceId, setSelectedDataSourceId] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'success' | 'failed'>('all');

  // Identify data sources that are part of this pipeline
  const pipelineDataSources = useMemo(() => {
    return pipelineExecutionState?.steps?.filter((s) => s.entityType === EntityType.dataSourceInstance) || [];
  }, [pipelineExecutionState]);

  // Identify source agents that are part of this pipeline
  const pipelineAgents = useMemo(() => {
    return pipelineExecutionState?.steps?.filter((s) => s.entityType === EntityType.agentInstance) || [];
  }, [pipelineExecutionState]);

  // Fetch items via getUserAppInstanceItemProcessingStatuses
  const fetchItems = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const data = await apiContext.getUserAppInstanceItemProcessingStatuses(
        step.entityDefId,
        step.entityId,
        pipelineExecutionState?.pipelineInstanceId,
      );
      setRawItems(data || []);
    } catch (err: any) {
      console.error('Error fetching user app items:', err);
      setFetchError(err?.response?.data?.message || err?.message || 'Failed to fetch items for this User App.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (step && (step.entityDefId || step.entityId)) {
      fetchItems();
    }
  }, [step?.entityDefId, step?.entityId, pipelineExecutionState?.id, stepExecutionResponse]);

  // Scope items strictly to:
  // 1. Source agents in this pipeline
  // 2. Data sources in this pipeline
  const pipelineScopedItems = useMemo(() => {
    return rawItems
      .filter((item) => {
        // 1. Match pipeline source agent (if pipeline has agents)
        if (pipelineAgents.length > 0) {
          const itemAgentId = item.sourceEntityId || item.entityId;
          const itemAgentDefId = item.sourceEntityDefId || item.entityDefId;
          const matchAgent = pipelineAgents.some(
            (a) =>
              (itemAgentId && String(a.entityId) === String(itemAgentId)) ||
              (itemAgentDefId && String(a.entityDefId) === String(itemAgentDefId)),
          );
          if (!matchAgent) return false;
        }

        // 2. Match pipeline data source (if pipeline has data sources)
        if (pipelineDataSources.length > 0) {
          const matchDs = pipelineDataSources.some(
            (ds) =>
              String(ds.entityDefId) === String(item.dataSourceDefId) &&
              String(ds.entityId) === String(item.dataSourceInstanceId),
          );
          if (!matchDs) return false;
        }

        return true;
      })
      .map((item) => {
        const itemAgentId = item.sourceEntityId || item.entityId;
        const itemAgentDefId = item.sourceEntityDefId || item.entityDefId;
        const matchedAgent = pipelineAgents.find(
          (a) =>
            (itemAgentId && String(a.entityId) === String(itemAgentId)) ||
            (itemAgentDefId && String(a.entityDefId) === String(itemAgentDefId)),
        );
        return {
          ...item,
          sourceEntityId: itemAgentId || matchedAgent?.entityId,
          sourceEntityDefId: itemAgentDefId || matchedAgent?.entityDefId,
          sourceAgentName:
            item.sourceEntityName ||
            item.sourceAgentName ||
            matchedAgent?.name ||
            matchedAgent?.entityName ||
            matchedAgent?.entityDefName ||
            'Agent',
        };
      });
  }, [rawItems, pipelineAgents, pipelineDataSources]);

  // Distinct Source Agents available among scoped items
  const distinctSourceAgents = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of pipelineScopedItems) {
      const id = String(item.sourceEntityId || item.entityId);
      const name = item.sourceAgentName || 'Agent';
      if (id && !map.has(id)) {
        map.set(id, name);
      }
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [pipelineScopedItems]);

  // Distinct Data Sources available among scoped items
  const distinctDataSources = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of pipelineScopedItems) {
      const id = String(item.dataSourceInstanceId);
      const name = item.dataSourceName || id;
      if (id && !map.has(id)) {
        map.set(id, name);
      }
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [pipelineScopedItems]);

  // Filter toolbar logic
  const filteredItems = useMemo(() => {
    return pipelineScopedItems.filter((item) => {
      // Source Agent filter
      if (selectedSourceAgentId !== 'all') {
        const itemAgentId = String(item.sourceEntityId || item.entityId);
        if (itemAgentId !== selectedSourceAgentId) return false;
      }

      // Data Source filter
      if (selectedDataSourceId !== 'all' && String(item.dataSourceInstanceId) !== selectedDataSourceId) {
        return false;
      }

      // Status filter
      const isFailed =
        item.status?.toLowerCase() === 'failed' ||
        item.status?.toLowerCase() === 'error' ||
        Boolean(item.error) ||
        Boolean(item.dsError);

      if (statusFilter === 'success' && isFailed) return false;
      if (statusFilter === 'failed' && !isFailed) return false;

      // Search query
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const nameMatch = item.itemName?.toLowerCase().includes(query);
        const dsMatch = item.dataSourceName?.toLowerCase().includes(query);
        const agentMatch = item.sourceAgentName?.toLowerCase().includes(query);
        const errorMatch = item.error?.toLowerCase().includes(query);
        const idMatch = String(item.dataSourceItemId || item.id)
          .toLowerCase()
          .includes(query);
        if (!nameMatch && !dsMatch && !agentMatch && !errorMatch && !idMatch) return false;
      }

      return true;
    });
  }, [pipelineScopedItems, selectedSourceAgentId, selectedDataSourceId, statusFilter, searchQuery]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', minWidth: 0 }}>
      {isExecuting && (
        <Alert
          severity="info"
          icon={<CircularProgress size={16} color="inherit" />}
          sx={{
            mb: 1,
            py: 0.5,
            px: 1.5,
            fontSize: '0.78rem',
            bgcolor: '#eff6ff',
            border: '1px solid #bfdbfe',
            alignItems: 'center',
          }}
        >
          User App execution is currently running...
        </Alert>
      )}

      {/* Fetch Error Banner */}
      {fetchError && (
        <Box sx={{ mb: 1 }}>
          <ErrorMessage error={fetchError} />
        </Box>
      )}

      {/* COMPACT TOOLBAR */}
      <Box
        sx={{
          p: 1,
          mb: 1,
          bgcolor: '#ffffff',
          borderRadius: 1,
          border: '1px solid #e2e8f0',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          minWidth: 0,
        }}
      >
        {/* Processed Count Chip */}
        <Chip
          icon={<AppsIcon sx={{ fontSize: '13px !important' }} />}
          label={`${filteredItems.length} items`}
          size="small"
          color="primary"
          variant="filled"
          sx={{ height: 24, fontSize: '0.72rem', fontWeight: 700 }}
        />

        {/* Source Agent Selector (if multiple source agents exist) */}
        {distinctSourceAgents.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={selectedSourceAgentId}
              onChange={(e) => setSelectedSourceAgentId(e.target.value)}
              sx={{ height: 28, fontSize: '0.75rem', bgcolor: '#f8fafc' }}
            >
              <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>
                <em>All Source Agents ({distinctSourceAgents.length})</em>
              </MenuItem>
              {distinctSourceAgents.map((ag) => (
                <MenuItem key={ag.id} value={ag.id} sx={{ fontSize: '0.75rem' }}>
                  {ag.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Data Source Selector (if multiple data sources exist) */}
        {distinctDataSources.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              value={selectedDataSourceId}
              onChange={(e) => setSelectedDataSourceId(e.target.value)}
              sx={{ height: 28, fontSize: '0.75rem', bgcolor: '#f8fafc' }}
            >
              <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>
                <em>All Data Sources ({distinctDataSources.length})</em>
              </MenuItem>
              {distinctDataSources.map((ds) => (
                <MenuItem key={ds.id} value={ds.id} sx={{ fontSize: '0.75rem' }}>
                  {ds.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Search Field */}
        <TextField
          size="small"
          placeholder="Search items..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            flexGrow: 1,
            minWidth: 120,
            maxWidth: 220,
            '& .MuiInputBase-root': { height: 28, fontSize: '0.75rem' },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />

        {/* Status Filter Chips */}
        <Stack direction="row" spacing={0.5} sx={{ ml: 'auto' }}>
          <Chip
            label="All"
            size="small"
            onClick={() => setStatusFilter('all')}
            color={statusFilter === 'all' ? 'primary' : 'default'}
            variant={statusFilter === 'all' ? 'filled' : 'outlined'}
            sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600, cursor: 'pointer' }}
          />
          <Chip
            label="Success"
            size="small"
            onClick={() => setStatusFilter('success')}
            color={statusFilter === 'success' ? 'success' : 'default'}
            variant={statusFilter === 'success' ? 'filled' : 'outlined'}
            sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600, cursor: 'pointer' }}
          />
          <Chip
            label="Failed"
            size="small"
            onClick={() => setStatusFilter('failed')}
            color={statusFilter === 'failed' ? 'error' : 'default'}
            variant={statusFilter === 'failed' ? 'filled' : 'outlined'}
            sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600, cursor: 'pointer' }}
          />
        </Stack>
      </Box>

      {/* ITEMS TABLE */}
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            gap: 1,
          }}
        >
          <CircularProgress size={28} />
          <Typography variant="caption" color="text.secondary">
            Loading User App source items...
          </Typography>
        </Box>
      ) : filteredItems.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: '#ffffff',
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <SmartToyIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            No items found for this User App in this pipeline.
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Ensure upstream source agents have processed items from this pipeline's data sources.
          </Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            flexGrow: 1,
            borderRadius: 1,
            overflow: 'auto',
            minHeight: 180,
            border: '1px solid #e2e8f0',
          }}
        >
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.72rem', color: '#475569' } }}>
                <TableCell sx={{ width: 36, py: 0.75, px: 1 }} />
                <TableCell sx={{ py: 0.75, px: 1, width: '28%' }}>Item Name</TableCell>
                <TableCell sx={{ py: 0.75, px: 1, width: '20%' }}>Source Agent</TableCell>
                <TableCell sx={{ py: 0.75, px: 1, width: '20%' }}>Data Source</TableCell>
                <TableCell sx={{ py: 0.75, px: 1, width: 90 }}>Status</TableCell>
                <TableCell sx={{ py: 0.75, px: 1, width: 110 }}>Duration</TableCell>
                <TableCell sx={{ width: 75, py: 0.75, px: 1, textAlign: 'right' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.map((item, idx) => (
                <UserAppItemRow
                  key={`${item.id}-${item.sourceEntityId || item.entityId}-${idx}`}
                  item={item}
                  itemIndex={idx}
                  pipelineInstanceId={pipelineExecutionState?.pipelineInstanceId || pipelineExecutionState?.id}
                  onRestartFromPoint={onRestartFromPoint}
                  onItemDeleted={fetchItems}
                  onChange={onChange}
                  step={step}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default UserAppStepItemsView;
